import React, { useMemo } from 'react'
import PropTypes from 'prop-types'

import usePool from '@duckness/use-pool'
import InsightsDashboardPool from '@aytm/app/insights/dashboard/duckness/InsightsDashboardPool'

import { defaultColors } from '@aytm/app/insights/dashboard/lib/colors'
import { getCSSVar } from '@aytm/app/insights/dashboard/lib/css'

import { useAttributes } from '@aytm/app/insights/dashboard/components/hooks'

import { legendOptions, seriesName, showCollapsedSections, showAttributeMetrics, showUniqueAttributes } from '../config'

import { stateSelector, shouldUpdate } from './selectors'

import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'

import './Legend.css'

const defaultTextColor = getCSSVar('aytm-insights-color-text')

const CLASS = 'insights-portal-split-chart-legend'

const Legend = props => {
  const attributeMode = props.attributeMode

  const { sortByAttribute, sortByMethod, chartMethod } = usePool(InsightsDashboardPool, stateSelector, shouldUpdate)

  const { attributes, getCaptions, chartAttribute } = useAttributes()

  const className = useMemo(() => props.className || CLASS, [props.className])

  const style = useMemo(() => {
    return { ...(props.style || {}), width: `${props.width}px`, flex: `0 0 ${props.width}px` }
  }, [props.style, props.width])

  const options = useMemo(() => {
    // Create captions for attributes in all sections
    const captions = getCaptions({
      showSections: false,
      showMetrics: showAttributeMetrics,
      showMetadata: false,
      showCollapsed: showCollapsedSections,
      showUnique: showUniqueAttributes
    })

    // Create series
    const series = captions.map(({ section, attribute, metrics, disabled }, index) => {
      // Guard (chartAttribute !== sortByAttribute) prevents every metric sub-row of chartAttribute
      // from going bold when the user is already sorting by a non-meta attribute in that same group.
      const selected =
        (attribute === sortByAttribute && sortByMethod === (metrics?.id ?? null)) ||
        (attribute === chartAttribute && chartAttribute !== sortByAttribute && chartMethod === (metrics?.id ?? null))
      const visible = attributeMode ? selected : disabled.includes(metrics?.id) ? false : true
      const color = attributeMode ? defaultTextColor : visible ? defaultColors[index % defaultColors.length] : null
      return {
        name: seriesName(attributes, attribute, metrics, selected ? color : null),
        custom: {
          section,
          attribute,
          metrics,
          selected
        },
        data: [0],
        type: 'column',
        lineWidth: selected ? 3 : 1,
        color,
        shadow: selected
          ? {
              color,
              width: 7,
              offsetX: 0,
              offsetY: 0,
              opacity: 0.25
            }
          : false,
        marker: {
          symbol: 'circle',
          radius: 7
        },
        zIndex: selected ? 1 : 0,
        visible
      }
    })

    // Legend click event
    const onClick = event => {
      const { section, attribute, metrics } = event.target?.options?.custom || {}
      const method = metrics?.id || null
      props.onLegendClick?.({ section, attribute, method, toggle: attributeMode })
      return false
    }

    // Create legend options
    return legendOptions({
      series,
      hideMarkers: attributeMode,
      onClick
    })
  }, [attributeMode, attributes, getCaptions, props, sortByAttribute, sortByMethod, chartAttribute, chartMethod])

  return (
    <div className={className} style={style}>
      {options ? <HighchartsReact highcharts={Highcharts} options={options} /> : <div></div>}
    </div>
  )
}

Legend.propTypes = {
  className: PropTypes.string,
  style: PropTypes.object,
  width: PropTypes.number,
  wait: PropTypes.any,
  attributeMode: PropTypes.any,
  onLegendClick: PropTypes.func
}

export default Legend
